// ════════════════════════════════════════════════════════════════
// CONTENT FRAME — Canvas-Compatible Content Delivery Window
// ════════════════════════════════════════════════════════════════
//
// This component wraps the main content area. It provides:
// 1. A standard HTML5 Canvas element (always present, hidden when
//    not needed) that game engines can render to.
// 2. A React component slot for data-driven UI (forms, lists, charts).
//
// When you transition from a data app to a game, you:
// - Set config.canvas.mode to 'game'
// - The canvas becomes visible and fills the content area
// - The React children are hidden
// - Your game engine renders to the canvas
//
// The shell layout (header, nav, monetization) stays identical.
// ════════════════════════════════════════════════════════════════

import { useEffect, useRef, type ReactNode } from 'react';
import type { AppConfig, WidgetDefinition } from '../config/app.config';

interface ContentFrameProps {
  config: AppConfig;
  widget: WidgetDefinition;
  children: ReactNode;
}

export function ContentFrame({ config, widget, children }: ContentFrameProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isCanvasMode = config.canvas.enabled && (
    widget.type === 'canvas' || config.canvas.mode === 'game'
  );

  // ── Canvas render loop ──
  // For chart mode: renders a simple placeholder visualization
  // For game mode: this is where your game engine init would go
  useEffect(() => {
    if (!isCanvasMode || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size to match display
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    // ── RENDER LOOP ──
    // In chart mode: draw a simple allocation placeholder
    // In game mode: replace with your game's update/render functions
    let animationId: number;

    const render = () => {
      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);

      if (config.canvas.mode === 'chart') {
        renderChartPlaceholder(ctx, rect.width, rect.height, config);
      } else if (config.canvas.mode === 'game') {
        // ── GAME ENGINE HOOK ──
        // When transitioning to games, initialize your engine here:
        //   import { GameEngine } from '../game/engine';
        //   GameEngine.update(ctx, rect.width, rect.height);
        // For now, show a placeholder
        ctx.fillStyle = '#475569';
        ctx.font = '14px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Game engine slot — canvas ready', rect.width / 2, rect.height / 2);
      }

      animationId = requestAnimationFrame(render);
    };
    render();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, [isCanvasMode, config]);

  return (
    <div className="p-4 md:p-6 h-full">
      {/* ── Canvas layer (hidden in data mode, visible in canvas/game mode) ── */}
      <div
        className="canvas-host"
        style={{
          position: isCanvasMode ? 'relative' : 'absolute',
          width: isCanvasMode ? '100%' : 0,
          height: isCanvasMode ? `calc(100% - 0px)` : 0,
          opacity: isCanvasMode ? 1 : 0,
          pointerEvents: isCanvasMode ? 'auto' : 'none',
          overflow: 'hidden',
        }}
      >
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height: '100%', display: 'block' }}
        />
      </div>

      {/* ── React UI layer (data-driven content) ── */}
      <div
        style={{
          display: isCanvasMode ? 'none' : 'block',
          height: '100%',
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ── Chart Placeholder Renderer (native canvas drawing) ──
// This demonstrates the canvas is functional. In production,
// replace with Chart.js or a custom chart renderer.
function renderChartPlaceholder(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  config: AppConfig
) {
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(0, 0, width, height);

  // Draw a simple donut chart placeholder
  const cx = width / 2;
  const cy = height / 2;
  const radius = Math.min(width, height) * 0.3;

  const segments = [
    { color: '#3b82f6', value: 0.4 },
    { color: '#8b5cf6', value: 0.3 },
    { color: '#ec4899', value: 0.2 },
    { color: '#f59e0b', value: 0.1 },
  ];

  let startAngle = -Math.PI / 2;
  for (const seg of segments) {
    const endAngle = startAngle + seg.value * Math.PI * 2;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, startAngle, endAngle);
    ctx.arc(cx, cy, radius * 0.6, endAngle, startAngle, true);
    ctx.closePath();
    ctx.fillStyle = seg.color;
    ctx.fill();
    startAngle = endAngle;
  }

  ctx.fillStyle = '#94a3b8';
  ctx.font = '12px Inter, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Portfolio Allocation', cx, cy + 5);
}
